/**
 * useResolveIssuesFlow — kick off the bulk "Resolve issues" run, drive it on the
 * spine, and let the writer pause / stop / resume it.
 *
 * The backend reuses the existing run_id + SSE stream (see
 * ``POST /runs/{id}/resolve-issues``), so the resolution renders live on the
 * spine with zero new SSE code. On start we eagerly paint each target section
 * ``drafting``, clear the terminal-run gate + bump the subscription nonce so
 * ``useVariantCRun`` re-subscribes to the same run, switch to the Spine view,
 * then POST.
 *
 * The pass deliberately never enters the reducer's mid-run state (so the run
 * Stop/Pause bar stays hidden — those cancel the DRAFTING run, which this is
 * not). Instead this hook owns a small phase machine fed by the resolve SSE
 * events, and its own control bar:
 *
 *   idle → running → (pausing|stopping) → paused → running (resume) → … → idle
 *
 * ``pause`` asks the server to stop at the next safe point and report the
 * remaining targets on ``resolve_stopped``; ``resume`` re-POSTs exactly those.
 * ``stop`` cancels and returns to idle.
 */

import type { Dispatch, SetStateAction } from "react";
import { useCallback, useRef, useState } from "react";
import { controlResolveIssues, type ResolveIssueDoc, resolveIssues } from "../../api/client";
import type { ProgressEvent } from "../../types";
import type { ResolveSelectionTarget } from "../components/SubmissionIssuesTab";
import type { SpineSectionStatus } from "../sseAdapter";

export type ResolvePhase = "idle" | "running" | "pausing" | "stopping" | "paused";

/** Minimal target shape the API takes (display fields stripped). */
interface ResolveApiTarget {
  module_id: string;
  issue_ids: string[];
}

export interface ResolveProgress {
  done: number;
  total: number;
}

export interface UseResolveIssuesFlowParams {
  runId: string | null;
  /** Eager paint: flip each target section's spine row to ``drafting``. */
  setSectionStatus: (sectionId: string, status: SpineSectionStatus) => void;
  /** Clear the terminal-run SSE gate so useVariantCRun re-subscribes. */
  setTerminalRunStatus: (v: boolean) => void;
  /** Bump the subscription nonce so the SSE effect re-runs on the same run_id. */
  setRunSubscriptionNonce: Dispatch<SetStateAction<number>>;
  /** Set the SSE start mode — fallback when no cursor is given. */
  setSubscriptionStartMode: (mode: "history" | "latest") => void;
  /** Seed the exact stream position captured before dispatch. */
  seedStreamCursor: (id: string | null) => void;
  /** Switch the shell to the Spine view so the run is visible. */
  onSwitchToSpine: () => void;
}

export interface UseResolveIssuesFlowResult {
  busy: boolean;
  error: string | null;
  phase: ResolvePhase;
  progress: ResolveProgress;
  /** Sections left to do when paused (drives the control bar's "m left"). */
  remainingCount: number;
  /** Start resolving the given targets. Returns true when the POST was accepted. */
  startResolve: (targets: ResolveSelectionTarget[], documentScope: ResolveIssueDoc[]) => Promise<boolean>;
  /** Ask the pass to stop at its next safe point and remember the rest. */
  pause: () => void;
  /** Ask the pass to stop and discard the rest. */
  stop: () => void;
  /** Re-run exactly the targets left when paused. */
  resume: () => void;
  /** Drop a paused pass without resuming. */
  discard: () => void;
  /** Fold one SSE event into the phase machine — call from the shell's handler. */
  onEvent: (event: ProgressEvent) => void;
}

export function useResolveIssuesFlow({
  runId,
  setSectionStatus,
  setTerminalRunStatus,
  setRunSubscriptionNonce,
  setSubscriptionStartMode,
  seedStreamCursor,
  onSwitchToSpine,
}: UseResolveIssuesFlowParams): UseResolveIssuesFlowResult {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<ResolvePhase>("idle");
  const [progress, setProgress] = useState<ResolveProgress>({ done: 0, total: 0 });
  // What's left to redo after a pause — set from the resolve_stopped event.
  const [remaining, setRemaining] = useState<{ targets: ResolveApiTarget[]; documentScope: ResolveIssueDoc[] } | null>(
    null,
  );
  // Live document scope for the pass in flight (so a mid-pass pause keeps it).
  const scopeRef = useRef<ResolveIssueDoc[]>([]);

  const _post = useCallback(
    async (targets: ResolveApiTarget[], documentScope: ResolveIssueDoc[]): Promise<boolean> => {
      if (!runId || targets.length === 0) return false;
      setBusy(true);
      setError(null);
      try {
        for (const t of targets) setSectionStatus(t.module_id, "drafting");
        onSwitchToSpine();
        scopeRef.current = documentScope;
        setPhase("running");
        setProgress({ done: 0, total: targets.length });
        setRemaining(null);
        // POST FIRST — the endpoint calls event_bus.create_run (so the stream
        // exists before we re-subscribe; a subscribe before it would 404, which
        // EventSource never retries) and returns the stream cursor captured just
        // before the orchestrator was dispatched.
        const resp = await resolveIssues(runId, { targets, document_scope: documentScope });
        // NOW re-subscribe FROM THE CURSOR so we replay module_start onward with
        // no tail race and no old-history replay.
        setSubscriptionStartMode("latest");
        seedStreamCursor(resp.stream_cursor ?? null);
        setTerminalRunStatus(false);
        setRunSubscriptionNonce((n) => n + 1);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("idle");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [
      runId,
      setSectionStatus,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setSubscriptionStartMode,
      seedStreamCursor,
      onSwitchToSpine,
    ],
  );

  const startResolve = useCallback(
    (targets: ResolveSelectionTarget[], documentScope: ResolveIssueDoc[]) =>
      // Strip display-only fields (number/title) — the API takes module_id + issue_ids.
      _post(
        targets.map((t) => ({ module_id: t.module_id, issue_ids: t.issue_ids })),
        documentScope,
      ),
    [_post],
  );

  const _control = useCallback(
    (action: "pause" | "cancel", optimistic: ResolvePhase) => {
      if (!runId) return;
      setPhase(optimistic);
      void controlResolveIssues(runId, action).catch((err) => {
        // A 404 means the pass already ended between the click and the request
        // — harmless; the terminal event will settle the phase.
        setError(err instanceof Error ? err.message : String(err));
      });
    },
    [runId],
  );

  const pause = useCallback(() => _control("pause", "pausing"), [_control]);
  const stop = useCallback(() => _control("cancel", "stopping"), [_control]);

  const resume = useCallback(() => {
    if (!remaining) return;
    void _post(remaining.targets, remaining.documentScope);
  }, [_post, remaining]);

  const discard = useCallback(() => {
    setPhase("idle");
    setRemaining(null);
  }, []);

  const onEvent = useCallback((event: ProgressEvent) => {
    const type = event.event_type;
    if (type === "module_start") {
      setProgress((p) => ({ ...p, total: Math.max(p.total, (event.module_total ?? p.total) || p.total) }));
      return;
    }
    if (type === "resolve_section_proposed" || type === "resolve_section_failed" || type === "resolve_section_skipped") {
      // A section finished the precompute pass (proposals ready to review) or
      // failed — either way it's done for progress. A skip (stopped before
      // completion) is not counted.
      if (type !== "resolve_section_skipped") setProgress((p) => ({ ...p, done: p.done + 1 }));
      return;
    }
    if (type === "resolve_stopped") {
      const p = (event.section_response ?? {}) as Record<string, unknown>;
      const mode = typeof p.mode === "string" ? p.mode : "cancel";
      const rt = Array.isArray(p.remaining_targets) ? (p.remaining_targets as ResolveApiTarget[]) : [];
      const ds = Array.isArray(p.document_scope) ? (p.document_scope as ResolveIssueDoc[]) : scopeRef.current;
      if (mode === "pause" && rt.length > 0) {
        setRemaining({ targets: rt, documentScope: ds });
        setPhase("paused");
      } else {
        setRemaining(null);
        setPhase("idle");
      }
      return;
    }
    if (type === "pipeline_complete") {
      // A stop lands its own resolve_stopped first (→ paused/idle); a clean
      // finish arrives here still in "running".
      setPhase((cur) => (cur === "running" || cur === "stopping" ? "idle" : cur));
      return;
    }
  }, []);

  return {
    busy,
    error,
    phase,
    progress,
    remainingCount: remaining?.targets.length ?? 0,
    startResolve,
    pause,
    stop,
    resume,
    discard,
    onEvent,
  };
}
